import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import {
  Check, X, RefreshCw, AlertTriangle, Clock, RotateCcw, Trophy,
  Frown, Search, FileDown, ListChecks, Square, CheckSquare,
  TrendingDown, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { fmtBDT } from "../lib/format";

/**
 * Stuck Bets — admin tool for resolving pending bets whose DST SETTLE
 * callback was lost. v2 with search, bulk select, CSV export, sort.
 *
 * Backend endpoints:
 *   GET  /api/admin/pending-bets?min_age_minutes=…&limit=…
 *   POST /api/admin/resolve-pending-bet  { bet_id, outcome, win_amount, admin_note }
 */
export default function StuckBets() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [minAge, setMinAge] = useState(30);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [acting, setActing] = useState({});         // bet_id → boolean (single)
  const [bulkActing, setBulkActing] = useState(false);
  const [selected, setSelected] = useState({});     // bet_id → true
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("oldest");  // oldest | newest | highest_stake
  const [lastRefresh, setLastRefresh] = useState(null);
  const [exporting, setExporting] = useState(false);
  const timerRef = useRef(null);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) setRefreshing(true); else setLoading(true);
      try {
        const { data } = await api.get("/admin/pending-bets", {
          params: { min_age_minutes: minAge, limit: 1000 },
        });
        setRows(data?.items || []);
        setLastRefresh(new Date());
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load stuck bets");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [minAge]
  );

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoRefresh) {
      timerRef.current = setInterval(() => load({ silent: true }), 60_000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [autoRefresh, load]);

  // Filter + sort
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows;
    if (q) {
      list = rows.filter((r) =>
        [r.username, r.player_id, r.round_id, r.txn_id, r.game, r.game_uid]
          .some((v) => (v || "").toString().toLowerCase().includes(q))
      );
    }
    const copy = [...list];
    if (sortOrder === "oldest") {
      copy.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else if (sortOrder === "newest") {
      copy.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortOrder === "highest_stake") {
      copy.sort((a, b) => Number(b.stake || 0) - Number(a.stake || 0));
    }
    return copy;
  }, [rows, search, sortOrder]);

  const totalStake = useMemo(
    () => visibleRows.reduce((s, r) => s + Number(r.stake || 0), 0),
    [visibleRows]
  );

  const selectedIds = useMemo(
    () => Object.keys(selected).filter((k) => selected[k]),
    [selected]
  );

  const toggleSelect = (id) =>
    setSelected((p) => ({ ...p, [id]: !p[id] }));
  const selectAll = () => {
    if (selectedIds.length === visibleRows.length && visibleRows.length > 0) {
      setSelected({});
    } else {
      const next = {};
      visibleRows.forEach((r) => { next[r.id] = true; });
      setSelected(next);
    }
  };

  const resolve = async (bet, outcome, opts = {}) => {
    if (acting[bet.id]) return;
    let winAmount = 0;
    let note = opts.note || "";
    if (outcome === "won") {
      const raw = window.prompt(
        `Resolve "${bet.game || bet.round_id}" as WON.\n\nTotal payout to player (including stake of ৳${bet.stake})?`,
        String(bet.stake * 2 || 0)
      );
      if (!raw) return;
      winAmount = Number(raw);
      if (!isFinite(winAmount) || winAmount <= 0) {
        toast.error("Enter a positive win amount");
        return;
      }
    }
    if (!opts.note && (outcome === "won" || outcome === "refund" || outcome === "void")) {
      note = window.prompt(
        `Add an admin note (visible in audit log):`,
        outcome === "won" ? "DST SETTLE callback missed — manual settle" :
        outcome === "refund" ? "Match cancelled / round voided" :
        "Manual void"
      ) || "";
      if (note === null) return;
    }
    setActing((p) => ({ ...p, [bet.id]: true }));
    try {
      const { data } = await api.post("/admin/resolve-pending-bet", {
        bet_id: bet.id, outcome, win_amount: winAmount, admin_note: note,
      });
      toast.success(
        `Resolved as ${data.outcome.toUpperCase()} · credit ৳${data.balance_credit} → balance ৳${data.new_balance}`
      );
      setRows((p) => p.filter((r) => r.id !== bet.id));
      setSelected((p) => { const n = { ...p }; delete n[bet.id]; return n; });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resolve");
    } finally {
      setActing((p) => { const n = { ...p }; delete n[bet.id]; return n; });
    }
  };

  const bulkResolveLost = async () => {
    if (bulkActing || selectedIds.length === 0) return;
    if (!window.confirm(
      `Mark ${selectedIds.length} bet${selectedIds.length === 1 ? "" : "s"} as LOST?\n\n` +
      `Stake total: ৳${totalStakeOf(rows, selectedIds).toLocaleString()}.\n` +
      `No balance change — stake was already debited at BET time. ` +
      `This is the safe default for stuck slot/casino losses.`
    )) return;
    setBulkActing(true);
    let success = 0, failed = 0;
    for (const id of selectedIds) {
      try {
        await api.post("/admin/resolve-pending-bet", {
          bet_id: id, outcome: "lost",
          admin_note: "Bulk-resolve: stuck pending, no DST settle received",
        });
        success += 1;
      } catch { failed += 1; }
    }
    toast.success(`Bulk resolve: ${success} marked LOST${failed ? ` · ${failed} failed` : ""}`);
    await load({ silent: true });
    setSelected({});
    setBulkActing(false);
  };

  const exportPdf = async () => {
    if (visibleRows.length === 0) return;
    setExporting(true);
    try {
      const res = await api.get("/admin/pending-bets.pdf", {
        params: { min_age_minutes: minAge, search, limit: 5000 },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `stuck-bets-${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported PDF — ${visibleRows.length} bet${visibleRows.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  const oldestMin = visibleRows.length
    ? Math.max(...visibleRows.map((r) => minutesAgo(r.created_at)))
    : 0;

  return (
    <div data-testid="stuck-bets-page">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <ShieldAlert size={22} className="text-amber-600" />
            Stuck Bets
          </span>
        }
        subtitle={
          lastRefresh
            ? `Last refresh ${lastRefresh.toLocaleTimeString("en-GB")} · ${autoRefresh ? "auto every 60s" : "manual"}`
            : "Loading…"
        }
        testId="stuck-bets-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => load({ silent: true })}
              disabled={refreshing}
              className="h-9 px-3 inline-flex items-center gap-1.5 border border-gray-300 rounded-sm text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
              data-testid="stuck-bets-refresh"
            >
              <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={exportPdf}
              disabled={visibleRows.length === 0 || exporting}
              className="h-9 px-3 inline-flex items-center gap-1.5 border border-gray-300 rounded-sm text-xs font-medium hover:bg-gray-50 disabled:opacity-40"
              data-testid="stuck-bets-export"
              title="Download a branded PDF of the visible bets"
            >
              <FileDown size={13} className={exporting ? "animate-pulse" : ""} />
              {exporting ? "Generating…" : "Export PDF"}
            </button>
          </div>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <KpiCard
          icon={AlertTriangle}
          label="Stuck Bets"
          value={visibleRows.length.toLocaleString()}
          accent={visibleRows.length > 0 ? "amber" : "emerald"}
          testId="stuck-bets-count"
          hint={search ? `${rows.length} total · ${visibleRows.length} match` : "Older than threshold"}
        />
        <KpiCard
          icon={TrendingDown}
          label="Total Stake at Risk"
          value={fmtBDT(totalStake)}
          accent="slate"
          testId="stuck-bets-total-stake"
          hint="Sum of all visible stakes"
        />
        <KpiCard
          icon={Clock}
          label="Oldest Pending"
          value={visibleRows.length ? formatAge(oldestMin) : "—"}
          accent={oldestMin >= 180 ? "rose" : oldestMin >= 60 ? "amber" : "slate"}
          testId="stuck-bets-oldest"
          hint="From oldest visible row"
        />
      </div>

      {/* Filter / search toolbar */}
      <div className="bg-white border border-gray-200 rounded-sm mb-3" data-testid="stuck-bets-toolbar">
        <div className="p-3 flex flex-col lg:flex-row lg:items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by player, round ID, transaction ID, game…"
              className="w-full h-9 pl-9 pr-9 border border-gray-300 rounded-sm text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
              data-testid="stuck-bets-search"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-700"
                aria-label="Clear search"
                data-testid="stuck-bets-search-clear"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
              <Clock size={13} className="text-gray-400" /> Age
              <select
                value={minAge}
                onChange={(e) => setMinAge(Number(e.target.value))}
                className="h-8 px-2 border border-gray-300 rounded-sm text-xs bg-white focus:outline-none focus:border-emerald-500"
                data-testid="stuck-bets-min-age"
              >
                <option value={15}>≥ 15 min</option>
                <option value={30}>≥ 30 min</option>
                <option value={60}>≥ 1 hour</option>
                <option value={180}>≥ 3 hours</option>
                <option value={360}>≥ 6 hours</option>
                <option value={720}>≥ 12 hours</option>
                <option value={1440}>≥ 24 hours</option>
              </select>
            </label>

            <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
              Sort
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="h-8 px-2 border border-gray-300 rounded-sm text-xs bg-white focus:outline-none focus:border-emerald-500"
                data-testid="stuck-bets-sort"
              >
                <option value="oldest">Oldest first</option>
                <option value="newest">Newest first</option>
                <option value="highest_stake">Highest stake</option>
              </select>
            </label>

            <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 select-none cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="accent-emerald-600 h-3.5 w-3.5"
                data-testid="stuck-bets-auto-refresh"
              />
              Auto-refresh
            </label>
          </div>
        </div>

        {/* Bulk action bar */}
        {selectedIds.length > 0 && (
          <div className="px-3 py-2 border-t border-gray-200 bg-amber-50/60 flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid="stuck-bets-bulk-bar">
            <div className="text-xs text-amber-900 inline-flex items-center gap-1.5">
              <ListChecks size={14} />
              <strong>{selectedIds.length}</strong> selected · ৳{totalStakeOf(rows, selectedIds).toLocaleString()} total stake
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelected({})}
                className="h-8 px-3 text-xs border border-gray-300 rounded-sm hover:bg-white"
                data-testid="stuck-bets-bulk-clear"
              >
                Clear
              </button>
              <button
                onClick={bulkResolveLost}
                disabled={bulkActing}
                className="h-8 px-3 inline-flex items-center gap-1.5 text-xs font-semibold bg-red-600 hover:bg-red-700 text-white rounded-sm disabled:opacity-50"
                data-testid="stuck-bets-bulk-lost"
              >
                <Frown size={13} />
                {bulkActing ? "Resolving…" : `Mark all ${selectedIds.length} as LOST`}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table card */}
      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="stuck-bets-table-wrap">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500" data-testid="stuck-bets-loading">
            <div className="inline-block animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full mb-3" />
            <div>Loading stuck bets…</div>
          </div>
        ) : visibleRows.length === 0 ? (
          <EmptyState search={search} minAge={minAge} clear={() => setSearch("")} />
        ) : (
          <>
            <table className="hidden md:table w-full text-sm" data-testid="stuck-bets-table">
              <thead className="bg-gray-50/80 text-[11px] uppercase tracking-wider text-gray-500 select-none">
                <tr>
                  <th className="w-10 px-3 py-2.5">
                    <button
                      onClick={selectAll}
                      className="inline-flex items-center justify-center p-0.5 text-gray-500 hover:text-gray-900"
                      data-testid="stuck-bets-select-all"
                      aria-label="Select all"
                    >
                      {selectedIds.length === visibleRows.length && visibleRows.length > 0
                        ? <CheckSquare size={16} className="text-emerald-600" />
                        : <Square size={16} />}
                    </button>
                  </th>
                  <th className="px-3 py-2.5 text-left font-semibold">Player</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Game / Round</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Stake</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Age</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Placed</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Resolve</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleRows.map((r) => {
                  const sel = !!selected[r.id];
                  return (
                    <tr
                      key={r.id}
                      data-testid={`stuck-bet-row-${r.id}`}
                      className={`hover:bg-amber-50/40 transition-colors ${sel ? "bg-amber-50/60" : ""}`}
                    >
                      <td className="px-3 py-2.5">
                        <button
                          onClick={() => toggleSelect(r.id)}
                          className="inline-flex items-center justify-center p-0.5 text-gray-500 hover:text-emerald-600"
                          data-testid={`stuck-bet-select-${r.id}`}
                          aria-label={sel ? "Deselect" : "Select"}
                        >
                          {sel
                            ? <CheckSquare size={16} className="text-emerald-600" />
                            : <Square size={16} />}
                        </button>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-gray-900">{r.username || "—"}</div>
                        {r.player_id ? <div className="text-[11px] text-gray-500 font-mono">{r.player_id}</div> : null}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-gray-800">{r.game || r.game_uid || "—"}</div>
                        <div className="text-[11px] text-gray-500 font-mono truncate max-w-[220px]" title={r.round_id || r.txn_id}>
                          {r.round_id || r.txn_id}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <span className="font-mono font-semibold text-gray-900">{fmtBDT(r.stake)}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        <AgePill minutes={minutesAgo(r.created_at)} />
                      </td>
                      <td className="px-3 py-2.5 text-gray-600 text-xs whitespace-nowrap">
                        {new Date(r.created_at).toLocaleString("en-GB")}
                      </td>
                      <td className="px-3 py-2.5">
                        <ResolveButtons row={r} acting={!!acting[r.id]} resolve={resolve} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {visibleRows.length > 6 && (
                <tfoot>
                  <tr className="border-t border-gray-200 bg-gray-50/60">
                    <td colSpan={3} className="px-3 py-2 text-xs text-gray-600">
                      {visibleRows.length} {visibleRows.length === 1 ? "row" : "rows"}
                    </td>
                    <td className="px-3 py-2 text-right text-xs font-semibold text-gray-900">
                      {fmtBDT(totalStake)}
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              )}
            </table>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {visibleRows.map((r) => {
                const sel = !!selected[r.id];
                return (
                  <div
                    key={r.id}
                    className={`p-3 ${sel ? "bg-amber-50/60" : ""}`}
                    data-testid={`stuck-bet-card-${r.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0">
                        <button
                          onClick={() => toggleSelect(r.id)}
                          className="mt-1 shrink-0 text-gray-500"
                          data-testid={`stuck-bet-select-${r.id}`}
                        >
                          {sel ? <CheckSquare size={16} className="text-emerald-600" /> : <Square size={16} />}
                        </button>
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-900 truncate">{r.username || "—"}</div>
                          <div className="text-xs text-gray-600 truncate">{r.game || r.game_uid}</div>
                          <div className="text-[10px] text-gray-400 font-mono truncate">{r.round_id || r.txn_id}</div>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono font-semibold text-sm">{fmtBDT(r.stake)}</div>
                        <div className="mt-1"><AgePill minutes={minutesAgo(r.created_at)} /></div>
                      </div>
                    </div>
                    <div className="mt-2.5">
                      <ResolveButtons row={r} acting={!!acting[r.id]} resolve={resolve} />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */
function minutesAgo(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
}
function formatAge(min) {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60); const m = min % 60;
  if (h < 24) return `${h}h ${m}m`;
  const d = Math.floor(h / 24); return `${d}d ${h % 24}h`;
}
function totalStakeOf(rows, ids) {
  const idSet = new Set(ids);
  return rows.filter((r) => idSet.has(r.id)).reduce((s, r) => s + Number(r.stake || 0), 0);
}

/* ---------- small components ---------- */
function AgePill({ minutes }) {
  let cls = "bg-amber-100 text-amber-800";
  if (minutes >= 60 * 24) cls = "bg-red-100 text-red-800";
  else if (minutes >= 60 * 3) cls = "bg-red-50 text-red-700";
  else if (minutes >= 60) cls = "bg-orange-100 text-orange-800";
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-sm ${cls}`}>
      <Clock size={10} />
      {formatAge(minutes)}
    </span>
  );
}

function ResolveButtons({ row, acting, resolve }) {
  return (
    <div className="flex items-center gap-1 justify-end flex-wrap">
      <button
        onClick={() => resolve(row, "lost", { note: "Stuck pending — auto-marked lost" })}
        disabled={acting}
        className="h-7 px-2 inline-flex items-center gap-1 text-[11px] font-semibold border border-red-200 text-red-700 rounded-sm hover:bg-red-50 hover:border-red-300 disabled:opacity-40 transition-colors"
        data-testid={`resolve-lost-${row.id}`}
        title="Mark as LOST — no balance change (most common for stuck slot losses)"
      >
        <Frown size={11} /> Lost
      </button>
      <button
        onClick={() => resolve(row, "won")}
        disabled={acting}
        className="h-7 px-2 inline-flex items-center gap-1 text-[11px] font-semibold border border-emerald-200 text-emerald-700 rounded-sm hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-40 transition-colors"
        data-testid={`resolve-won-${row.id}`}
        title="Mark as WON — prompts for win amount, credits player balance"
      >
        <Trophy size={11} /> Won
      </button>
      <button
        onClick={() => resolve(row, "refund")}
        disabled={acting}
        className="h-7 px-2 inline-flex items-center gap-1 text-[11px] font-semibold border border-amber-200 text-amber-700 rounded-sm hover:bg-amber-50 hover:border-amber-300 disabled:opacity-40 transition-colors"
        data-testid={`resolve-refund-${row.id}`}
        title="Refund — returns the stake to the player's balance"
      >
        <RotateCcw size={11} /> Refund
      </button>
      <button
        onClick={() => resolve(row, "void")}
        disabled={acting}
        className="h-7 px-2 inline-flex items-center gap-1 text-[11px] font-semibold border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-100 disabled:opacity-40 transition-colors"
        data-testid={`resolve-void-${row.id}`}
        title="Void — returns the stake and marks bet cancelled"
      >
        <X size={11} /> Void
      </button>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent = "slate", hint, testId }) {
  const tones = {
    emerald: { border: "border-emerald-200", grad: "from-emerald-50 to-white", iconBg: "bg-emerald-100 text-emerald-700", labelClr: "text-emerald-800" },
    amber:   { border: "border-amber-200",   grad: "from-amber-50 to-white",   iconBg: "bg-amber-100 text-amber-700",   labelClr: "text-amber-800" },
    rose:    { border: "border-rose-200",    grad: "from-rose-50 to-white",    iconBg: "bg-rose-100 text-rose-700",     labelClr: "text-rose-800" },
    slate:   { border: "border-gray-200",    grad: "from-gray-50 to-white",    iconBg: "bg-gray-100 text-gray-700",     labelClr: "text-gray-700" },
  };
  const t = tones[accent] || tones.slate;
  return (
    <div
      className={`relative overflow-hidden border ${t.border} bg-gradient-to-br ${t.grad} rounded-sm p-4 flex items-center gap-3`}
      data-testid={testId}
    >
      <div className={`shrink-0 w-10 h-10 rounded-sm ${t.iconBg} inline-flex items-center justify-center`}>
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <div className={`text-[10px] uppercase tracking-widest font-bold ${t.labelClr}`}>{label}</div>
        <div className="font-display text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
        {hint ? <div className="text-[11px] text-gray-500 mt-0.5">{hint}</div> : null}
      </div>
    </div>
  );
}

function EmptyState({ search, minAge, clear }) {
  if (search) {
    return (
      <div className="p-10 text-center" data-testid="stuck-bets-empty">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 text-gray-500 mb-3">
          <Search size={22} />
        </div>
        <p className="text-sm font-semibold text-gray-800">No results for &quot;{search}&quot;</p>
        <p className="text-xs text-gray-500 mt-1">Try a different search term or clear the filter.</p>
        <button
          onClick={clear}
          className="mt-3 h-8 px-3 inline-flex items-center gap-1 text-xs border border-gray-300 rounded-sm hover:bg-gray-50"
          data-testid="stuck-bets-empty-clear"
        >
          <X size={12} /> Clear search
        </button>
      </div>
    );
  }
  return (
    <div className="p-10 text-center" data-testid="stuck-bets-empty">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 mb-3">
        <Check size={22} />
      </div>
      <p className="text-sm font-semibold text-gray-800">All clear!</p>
      <p className="text-xs text-gray-500 mt-1">
        No pending bets older than {minAge} minutes. The DST callback flow is healthy.
      </p>
    </div>
  );
}
